import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import {
  X, Copy, Check, Trash, WhatsappLogo, ArrowsClockwise,
  Sparkle, Clock, CheckCircle, UserPlus, ShieldCheck, Warning,
} from '@phosphor-icons/react';

/**
 * Admin modal for inviting a family member via QR + passkey.
 * Generates a single-use 24h invite link, shows it as a QR code, and lists
 * pending invites with revoke/approve controls.
 */
export default function InviteFamilyModal({ open, onClose, onApproved }) {
  const { api } = useAuth();
  const [invites, setInvites] = useState([]);
  const [creating, setCreating] = useState(false);
  const [newInvite, setNewInvite] = useState(null);
  const [name, setName] = useState('');
  const [role, setRole] = useState('member');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState({});

  const fetchInvites = async () => {
    try {
      const res = await api.get('/invites');
      setInvites(res.data.invites || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { if (open) fetchInvites(); }, [open]); // eslint-disable-line

  if (!open) return null;

  const handleCreate = async () => {
    setError(''); setCreating(true);
    try {
      const res = await api.post('/invites', { suggested_name: name.trim(), role });
      // Build the invite URL from the current browser origin (more reliable
      // than the server's view through the proxy)
      const inviteUrl = `${window.location.origin}/invite/${res.data.token}`;
      setNewInvite({ ...res.data, invite_url: inviteUrl });
      setName(''); setRole('member');
      fetchInvites();
    } catch (e) { setError(e.response?.data?.detail || 'Could not create invite'); }
    setCreating(false);
  };

  const handleApprove = async (invite_id) => {
    setBusy(b => ({ ...b, [invite_id]: 'approve' }));
    try {
      await api.post(`/invites/${invite_id}/approve`);
      fetchInvites();
      if (onApproved) onApproved();
    } catch (e) { setError(e.response?.data?.detail || 'Approval failed'); }
    setBusy(b => ({ ...b, [invite_id]: null }));
  };

  const handleRevoke = async (invite_id) => {
    if (!window.confirm('Revoke this invite? The link will no longer work.')) return;
    setBusy(b => ({ ...b, [invite_id]: 'revoke' }));
    try {
      await api.delete(`/invites/${invite_id}`);
      fetchInvites();
    } catch (e) { setError(e.response?.data?.detail || 'Revoke failed'); }
    setBusy(b => ({ ...b, [invite_id]: null }));
  };

  const handleCopy = (txt) => {
    navigator.clipboard.writeText(txt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const pending = invites.filter(i => i.status === 'accepted');
  const active = invites.filter(i => i.status === 'pending');
  const past = invites.filter(i => ['approved', 'revoked', 'expired'].includes(i.status));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-3 sm:p-4 overflow-y-auto"
      style={{ background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
      data-testid="invite-family-modal">
      <div className="relative w-full max-w-2xl my-4 sm:my-8" onClick={e => e.stopPropagation()}>
        <div className="rounded-3xl overflow-hidden shadow-2xl"
          style={{ background: 'rgba(20,20,20,0.96)', border: '1px solid rgba(255,255,255,0.08)' }}>

          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-white/5">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#FF8E53] flex items-center justify-center">
                <UserPlus size={18} weight="bold" className="text-white" />
              </div>
              <div>
                <h3 className="font-bold text-white text-base sm:text-lg">Invite Family Member</h3>
                <p className="text-[10px] text-neutral-500">Tap-to-join via QR — no password needed</p>
              </div>
            </div>
            <button onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="close-invite-modal">
              <X size={20} />
            </button>
          </div>

          <div className="p-5 space-y-5">
            {error && <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-400">{error}</div>}

            {/* New invite QR display */}
            {newInvite ? (
              <div className="rounded-2xl p-5 text-center"
                style={{ background: 'rgba(255,107,53,0.05)', border: '1px solid rgba(255,107,53,0.25)' }}
                data-testid="new-invite-qr">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#FF6B35]/15 border border-[#FF6B35]/30 mb-3">
                  <Sparkle size={10} weight="fill" className="text-[#FF6B35]" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#FF6B35]">Invite Created</span>
                </div>
                <div className="bg-white p-4 rounded-2xl inline-block mb-4" style={{ boxShadow: '0 8px 24px rgba(255,107,53,0.3)' }}>
                  <QRCodeSVG value={newInvite.invite_url} size={196} level="M" />
                </div>
                <p className="text-xs text-neutral-300 mb-2">Ask them to scan with their phone camera</p>
                <p className="text-[10px] text-neutral-500 mb-3">Expires in 24 hours · Single-use</p>

                <div className="flex flex-col sm:flex-row gap-2">
                  <button onClick={() => handleCopy(newInvite.invite_url)} data-testid="copy-invite-link"
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-white/5 hover:bg-white/10 text-white text-xs font-semibold">
                    {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
                    {copied ? 'Copied!' : 'Copy link'}
                  </button>
                  <a href={`https://wa.me/?text=${encodeURIComponent(`You're invited to join our HomeCommand household! Tap to set up: ${newInvite.invite_url}`)}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-green-500/10 hover:bg-green-500/20 text-green-400 text-xs font-semibold"
                    data-testid="whatsapp-invite-link">
                    <WhatsappLogo size={14} weight="fill" /> Send on WhatsApp
                  </a>
                </div>
                <button onClick={() => setNewInvite(null)} className="mt-3 text-[11px] text-neutral-500 hover:text-white">
                  ← Create another invite
                </button>
              </div>
            ) : (
              /* Create form */
              <div>
                <p className="text-xs text-neutral-400 mb-3 leading-relaxed">
                  Generate a one-time link. They'll set up their passkey on their own device — Face ID or fingerprint, no passwords involved. You'll get notified to approve them.
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 mb-1.5 block font-semibold">Suggested name (optional)</label>
                    <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Apeksha"
                      data-testid="invite-name-suggest"
                      className="w-full px-3.5 py-2.5 rounded-xl bg-black/30 border border-white/8 text-white text-sm placeholder-neutral-600 outline-none focus:border-[#FF6B35]/50" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 mb-1.5 block font-semibold">Role</label>
                    <div className="grid grid-cols-3 gap-2" data-testid="invite-role-picker">
                      {[
                        { v: 'member', label: 'Member', tagline: 'Family' },
                        { v: 'admin', label: 'Admin', tagline: 'Full access' },
                        { v: 'child', label: 'Child', tagline: 'Limited' },
                      ].map(r => (
                        <button key={r.v} onClick={() => setRole(r.v)} type="button"
                          data-testid={`invite-role-${r.v}`}
                          className={`p-2.5 rounded-xl text-left transition-all ${role === r.v ? 'scale-[1.02]' : 'hover:bg-white/5'}`}
                          style={{
                            background: role === r.v ? 'rgba(255,107,53,0.12)' : 'rgba(255,255,255,0.03)',
                            border: `1px solid ${role === r.v ? '#FF6B35' : 'rgba(255,255,255,0.08)'}`,
                          }}>
                          <p className="text-xs font-bold text-white">{r.label}</p>
                          <p className="text-[9px] text-neutral-500 mt-0.5">{r.tagline}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  <button onClick={handleCreate} disabled={creating} data-testid="generate-invite-btn"
                    className="w-full py-3 rounded-full text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #FF6B35, #FF8E53)', boxShadow: '0 6px 20px rgba(255,107,53,0.35)' }}>
                    {creating ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Sparkle size={14} weight="fill" />}
                    Generate Invite Link
                  </button>
                </div>
              </div>
            )}

            {/* Pending approvals */}
            {pending.length > 0 && (
              <div className="rounded-2xl p-4" style={{ background: 'rgba(0,229,255,0.05)', border: '1px solid rgba(0,229,255,0.25)' }} data-testid="pending-approvals">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle size={14} weight="duotone" className="text-[#00E5FF]" />
                  <p className="text-xs font-bold text-[#00E5FF] uppercase tracking-wider">Awaiting Your Approval ({pending.length})</p>
                </div>
                <div className="space-y-2">
                  {pending.map(inv => (
                    <div key={inv.invite_id} className="flex items-center gap-3 p-3 rounded-xl bg-black/30 border border-white/5"
                      data-testid={`pending-invite-${inv.invite_id}`}>
                      <div className="w-9 h-9 rounded-full bg-[#00E5FF]/15 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-[#00E5FF]">{(inv.accepted_name || '?')[0].toUpperCase()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{inv.accepted_name}</p>
                        <p className="text-[10px] text-neutral-500">
                          {inv.role} · accepted {formatRelative(inv.accepted_at)}
                        </p>
                      </div>
                      <button onClick={() => handleApprove(inv.invite_id)}
                        disabled={busy[inv.invite_id] === 'approve'}
                        data-testid={`approve-invite-${inv.invite_id}`}
                        className="px-3 py-1.5 rounded-full bg-[#00E5FF] text-black text-xs font-bold hover:scale-[1.05] transition-transform disabled:opacity-50">
                        {busy[inv.invite_id] === 'approve' ? '...' : 'Approve'}
                      </button>
                      <button onClick={() => handleRevoke(inv.invite_id)} className="text-neutral-500 hover:text-red-400 p-1.5">
                        <Trash size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Active (not yet accepted) */}
            {active.length > 0 && (
              <div>
                <p className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 mb-2 font-semibold flex items-center gap-1.5">
                  <Clock size={11} weight="duotone" /> Active Invite Links ({active.length})
                </p>
                <div className="space-y-2">
                  {active.map(inv => (
                    <div key={inv.invite_id} className="flex items-center gap-2 p-2.5 rounded-xl bg-white/3 border border-white/5"
                      data-testid={`active-invite-${inv.invite_id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-neutral-300 truncate">
                          {inv.suggested_name || 'Unnamed'} <span className="text-neutral-600">· {inv.role}</span>
                        </p>
                        <p className="text-[10px] text-neutral-500">Expires {formatRelative(inv.expires_at)}</p>
                      </div>
                      <button onClick={() => handleRevoke(inv.invite_id)} className="text-neutral-500 hover:text-red-400 p-1.5">
                        <Trash size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Past invites */}
            {past.length > 0 && (
              <details className="text-xs">
                <summary className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 font-semibold cursor-pointer hover:text-neutral-300">
                  History ({past.length})
                </summary>
                <div className="space-y-1 mt-2 max-h-40 overflow-y-auto">
                  {past.map(inv => (
                    <div key={inv.invite_id} className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-white/3 text-[11px]">
                      <span className="text-neutral-400 flex-1 truncate">{inv.suggested_name || inv.accepted_name || '—'}</span>
                      <StatusBadge status={inv.status} />
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    approved: { color: '#22C55E', bg: 'rgba(34,197,94,0.12)', label: 'Approved', icon: <CheckCircle size={10} weight="fill" /> },
    revoked: { color: '#F87171', bg: 'rgba(248,113,113,0.12)', label: 'Revoked', icon: <X size={10} weight="bold" /> },
    expired: { color: '#A3A3A3', bg: 'rgba(163,163,163,0.12)', label: 'Expired', icon: <Clock size={10} weight="bold" /> },
  }[status] || { color: '#A3A3A3', bg: 'rgba(163,163,163,0.12)', label: status, icon: null };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
      style={{ background: map.bg, color: map.color }}>
      {map.icon} {map.label}
    </span>
  );
}

function formatRelative(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = (d.getTime() - Date.now()) / 1000;
    const abs = Math.abs(diff);
    const future = diff > 0;
    const r = (val, unit) => future ? `in ${val} ${unit}` : `${val} ${unit} ago`;
    if (abs < 60) return future ? 'soon' : 'just now';
    if (abs < 3600) return r(Math.floor(abs / 60), 'min');
    if (abs < 86400) return r(Math.floor(abs / 3600), 'hr');
    return r(Math.floor(abs / 86400), 'days');
  } catch { return iso; }
}
